import React, { useState, useRef } from 'react';
import ThemedIcon from '../common/ThemedIcon';
import Lottie from 'react-lottie';
import starRatingJson from '../../lottie/star-rating.json';
import successTickJson from '../../lottie/SuccessTickGreen.json';
import StarOutlinedIcon from '../../images/Claims-Page/StarOutlined.svg';
import StarActiveIcon from '../../images/Claims-Page/StarFilled.svg';
import closeWhiteIcon from '../../images/lab-test/Cross_Greyed.svg';
import Modal from '../../containers/common/Modal';
import { FlyInTransition } from '../transitions/transitions';
import styles from './ClaimFeedbackBottomSheet.module.scss';

const STAR_INDICES = [1, 2, 3, 4, 5];
const LOTTIE_ANIMATION_MS = 1000;

const STAR_LABELS = {
  1: '😞 Very Dissatisfied',
  2: '😕 Dissatisfied',
  3: '😐 Neutral',
  4: '😊 Satisfied',
  5: '😄 Very Satisfied',
};

const STAR_LOTTIE_OPTIONS = {
  loop: false,
  autoplay: true,
  animationData: starRatingJson,
  rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
};

const SUCCESS_LOTTIE_OPTIONS = {
  loop: true,
  autoplay: true,
  animationData: successTickJson,
  rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
};

const ClaimFeedbackBottomSheet = ({
  visible,
  onClose,
  reviewOptions,
  reimbursementId,
  entryPoint,
  onSubmitReview,
}) => {
  const [selectedRating, setSelectedRating] = useState(null);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [animatingStar, setAnimatingStar] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const textareaRef = useRef(null);

  const autoExpandTextarea = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  const handleRemarksChange = (e) => {
    const val = e.target.value;
    if (val.length <= 500) {
      setRemarks(val);
      autoExpandTextarea(textareaRef.current);
    }
  };

  const handleStarClick = (rating) => {
    setSelectedRating(rating);
    setAnimatingStar(rating);
    setSelectedReasons([]);
    setTimeout(() => setAnimatingStar(null), LOTTIE_ANIMATION_MS);
  };

  const toggleReason = (reasonId) => {
    setSelectedReasons((prev) =>
      prev.includes(reasonId)
        ? prev.filter((id) => id !== reasonId)
        : [...prev, reasonId]
    );
  };

  const handleSubmit = async () => {
    if (!selectedRating || submitting) return;
    setSubmitting(true);
    setSubmitError(false);
    try {
      const trimmedRemarks = remarks.trim();
      await onSubmitReview({
        sourceId: reimbursementId,
        rating: selectedRating,
        reasonIds: selectedReasons,
        ...(trimmedRemarks ? { remarks: trimmedRemarks } : {}),
        entryPoint,
      });
      setShowSuccess(true);
    } catch (e) {
      if (e?.data?.errorCode === 'ALREADY_SUBMITTED') {
        setShowSuccess(true);
      } else {
        setSubmitError(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedRating(null);
    setSelectedReasons([]);
    setRemarks('');
    setShowSuccess(false);
    onClose();
  };

  const currentOptions = reviewOptions?.[String(selectedRating)] || [];

  if (!visible) return null;

  return (
    <Modal>
      <div className={styles.overlay} onClick={handleClose}>
        <FlyInTransition in={visible} mountOnEnter unmountOnExit appear>
          {showSuccess ? (
            <div
              className={styles.successSheet}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.successAnimation}>
                <Lottie
                  options={SUCCESS_LOTTIE_OPTIONS}
                  width={180}
                  height={180}
                />
              </div>
              <div className={styles.successTitle}>
                Thank you for taking the time to share your feedback with us!
              </div>
              <div className={styles.successSubtitle}>
                We truly appreciate your insights and are always looking to
                improve.
              </div>
              <button className={styles.successBtn} onClick={handleClose}>
                Okay
              </button>
            </div>
          ) : (
            <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className={styles.sheetHeader}>
                <span className={styles.sheetTitle}>
                  Reimbursement Feedback
                </span>
                <button className={styles.closeBtn} onClick={handleClose}>
                  <ThemedIcon
                    src={closeWhiteIcon}
                    alt="close"
                    className={styles.closeIcon}
                  />
                </button>
              </div>

              {/* Body */}
              <div className={styles.sheetBody}>
                <div className={styles.questionSection}>
                  <div className={styles.questionHeader}>
                    <div className={styles.questionTitle}>
                      How was your Claims experience ?
                    </div>
                    <div className={styles.questionDescription}>
                      We'd love your feedback on your reimbursement experience
                      so far
                    </div>
                  </div>

                  <div className={styles.starsBox}>
                    {STAR_INDICES.map((i) => (
                      <div
                        key={i}
                        className={styles.starWrapper}
                        onClick={() => handleStarClick(i)}
                      >
                        {animatingStar === i && (
                          <div className={styles.lottieOverlay}>
                            <Lottie
                              options={STAR_LOTTIE_OPTIONS}
                              width={60}
                              height={60}
                            />
                          </div>
                        )}
                        <img
                          src={
                            selectedRating >= i
                              ? StarActiveIcon
                              : StarOutlinedIcon
                          }
                          className={styles.starIcon}
                          alt={`${i} star`}
                        />
                      </div>
                    ))}
                  </div>

                  {selectedRating && (
                    <div className={styles.starLabel}>
                      {STAR_LABELS[selectedRating]}
                    </div>
                  )}
                </div>

                {selectedRating && (
                  <>
                    <div className={styles.chipsSection}>
                      <div className={styles.helpLabel}>
                        Help us know more about your experience..
                      </div>
                      <div className={styles.pillsContainer}>
                        {currentOptions.map((opt) => (
                          <div
                            key={opt.reasonId}
                            className={`${styles.pill} ${
                              selectedReasons.includes(opt.reasonId)
                                ? styles.selected
                                : ''
                            }`}
                            onClick={() => toggleReason(opt.reasonId)}
                          >
                            <span className={styles.pillText}>
                              {opt.ratingText}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <textarea
                      ref={textareaRef}
                      className={styles.remarksBox}
                      placeholder="Enter Remarks"
                      value={remarks}
                      onChange={handleRemarksChange}
                      maxLength={500}
                    />
                  </>
                )}
              </div>

              {/* Footer */}
              {selectedRating && (
                <div className={styles.sheetFooter}>
                  {submitError && (
                    <div className={styles.errorText}>
                      Something went wrong. Please try again.
                    </div>
                  )}
                  <button
                    className={styles.submitBtn}
                    onClick={handleSubmit}
                    disabled={submitting || selectedReasons.length === 0}
                  >
                    {submitting ? 'Submitting...' : 'Submit Feedback'}
                  </button>
                </div>
              )}
            </div>
          )}
        </FlyInTransition>
      </div>
    </Modal>
  );
};

export default ClaimFeedbackBottomSheet;
